import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  User, 
  Mail, 
  Lock, 
  CreditCard, 
  FileText, 
  Trash2, 
  Crown, 
  CheckCircle, 
  AlertCircle,
  Download,
  Loader,
  Eye,
  EyeOff,
  RefreshCw,
  LogOut,
  Globe
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../services/supabaseClient';
import LanguageSelector from '../layout/LanguageSelector';

function ProfileTab() {
  const { user, signOut, updatePassword, deleteAccount } = useAuth();
  const { t } = useTranslation();
  
  // Password change state
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });
  
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  
  // Invoices state
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  
  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Load subscription and plans on mount
  useEffect(() => {
    loadSubscriptionData();
    loadInvoices();
  }, []);

  const loadSubscriptionData = async () => {
    setSubscriptionLoading(true);
    try {
      const [sub, plansList] = await Promise.all([
        supabase.getSubscription(),
        supabase.getSubscriptionPlans()
      ]);
      
      // If no subscription exists, create free one
      if (!sub) {
        const newSub = await supabase.ensureFreeSubscription();
        setSubscription(newSub);
      } else {
        setSubscription(sub);
      }
      
      setPlans(plansList);
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const loadInvoices = async () => {
    setInvoicesLoading(true);
    try {
      const invoiceList = await supabase.getInvoices();
      setInvoices(invoiceList);
    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMessage({ type: '', text: '' });

    // Validation
    if (!newPassword || !confirmNewPassword) {
      setPasswordMessage({ type: 'error', text: t('app.profile.password.errors.fillAllFields') });
      return;
    }

    if (newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: t('app.profile.password.errors.minLength') });
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordMessage({ type: 'error', text: t('app.profile.password.errors.noMatch') });
      return;
    }

    setPasswordLoading(true);
    try {
      await updatePassword(newPassword);
      setPasswordMessage({ type: 'success', text: t('app.profile.password.success') });
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      setPasswordMessage({ type: 'error', text: error.message || t('app.profile.password.errors.updateFailed') });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== t('app.profile.delete.confirmWord')) {
      return;
    }
    
    setShowDeleteModal(false);
    await deleteAccount();
  };

  const handleUpgrade = async (planId) => {
    // Find the plan to get its stripe_price_id
    const plan = plans.find(p => p.id === planId);
    if (!plan || !plan.stripe_price_id) {
      alert(t('app.profile.subscription.planUnavailable'));
      return;
    }

    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          priceId: plan.stripe_price_id,
          userId: user.id,
          userEmail: user.email
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || t('app.profile.subscription.checkoutError'));
      }
      
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert(t('app.profile.subscription.genericError', { message: error.message }));
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await fetch('/api/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || t('app.profile.subscription.portalError'));
      }
      
      // Redirect to Stripe Customer Portal
      window.location.href = data.url;
    } catch (error) {
      console.error('Portal error:', error);
      alert(t('app.profile.subscription.genericError', { message: error.message }));
    }
  };

  const formatPrice = (cents, currency = 'eur') => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(cents / 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'pro': return 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white';
      case 'basic': return 'bg-gradient-to-r from-sky-500 to-blue-500 text-white';
      default: return 'bg-gray-200 text-gray-700';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle size={12} className="mr-1" />{t('app.profile.status.active')}</span>;
      case 'canceled':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"><AlertCircle size={12} className="mr-1" />{t('app.profile.status.canceled')}</span>;
      case 'past_due':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"><AlertCircle size={12} className="mr-1" />{t('app.profile.status.pastDue')}</span>;
      default:
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const currentPlan = plans.find(p => p.id === subscription?.plan) || { name: t('app.profile.plan.free'), id: 'free' };

  return (
    <div className="space-y-6">
      {/* Preferences Section */}
      {/* Logout Button */}
      <div className="flex justify-start">
        <button
          onClick={signOut}
          className="flex items-center space-x-2 px-4 py-2 text-gray-700 border border-gray-300 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <LogOut size={18} />
          <span>{t('common.logout')}</span>
        </button>
      </div>

      {/* Preferences Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Globe className="mr-2 text-sky-600" size={20} />
            {t('app.profile.preferences')}
          </h2>
        </div>
        <div className="p-6">
          <LanguageSelector />
        </div>
      </div>

      {/* Account Settings Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <User className="mr-2 text-sky-600" size={20} />
            {t('app.profile.accountSettings')}
          </h2>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Email Display */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="inline mr-2" size={16} />
              {t('app.profile.emailAddress')}
            </label>
            <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
              {user?.email || t('app.profile.noEmail')}
            </div>
          </div>

          {/* Password Change Form */}
          <div className="border-t pt-6">
            <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
              <Lock className="mr-2 text-gray-600" size={18} />
              {t('app.profile.changePassword')}
            </h3>
            
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('app.profile.password.new')}
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('app.profile.password.confirm')}
                </label>
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none"
                />
              </div>

              {passwordMessage.text && (
                <div className={`p-3 rounded-lg text-sm ${
                  passwordMessage.type === 'success' 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {passwordMessage.type === 'success' ? <CheckCircle className="inline mr-2" size={16} /> : <AlertCircle className="inline mr-2" size={16} />}
                  {passwordMessage.text}
                </div>
              )}

              <button
                type="submit"
                disabled={passwordLoading}
                className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {passwordLoading ? (
                  <>
                    <Loader className="animate-spin mr-2" size={16} />
                    {t('app.profile.saveLoading')}
                  </>
                ) : (
                  t('app.profile.savePassword')
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Subscription Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Crown className="mr-2 text-sky-600" size={20} />
            {t('app.profile.subscription.title')}
          </h2>
        </div>

        <div className="p-6">
          {subscriptionLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="animate-spin text-sky-600" size={24} />
            </div>
          ) : (
            <>
              {/* Current Plan Display */}
              <div className="mb-6 p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">{t('app.profile.subscription.currentPlan')}</p>
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPlanBadgeColor(subscription?.plan)}`}>
                        {currentPlan.name}
                      </span>
                      {getStatusBadge(subscription?.status || 'active')}
                    </div>
                  </div>
                  
                  {subscription?.plan !== 'free' && (
                    <button
                      onClick={handleManageSubscription}
                      className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                    >
                      <RefreshCw size={16} className="mr-2" />
                      {t('app.profile.subscription.manage')}
                    </button>
                  )}
                </div>

                {subscription?.current_period_end && (
                  <p className="mt-3 text-sm text-gray-600">
                    {subscription?.cancel_at_period_end 
                      ? t('app.profile.subscription.endsAt', { date: formatDate(subscription.current_period_end) })
                      : t('app.profile.subscription.nextBilling', { date: formatDate(subscription.current_period_end) })
                    }
                  </p>
                )}
              </div>

              {/* Plan Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {plans.map((plan) => {
                  const isCurrentPlan = subscription?.plan === plan.id;
                  const features = typeof plan.features === 'string' ? JSON.parse(plan.features) : plan.features;
                  
                  return (
                    <div
                      key={plan.id}
                      className={`relative p-5 rounded-xl border-2 transition-all ${
                        isCurrentPlan 
                          ? 'border-sky-500 bg-sky-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {isCurrentPlan && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <span className="px-3 py-1 bg-sky-500 text-white text-xs font-medium rounded-full">
                            {t('app.profile.subscription.current')}
                          </span>
                        </div>
                      )}

                      <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                      
                      <div className="mt-4">
                        <span className="text-3xl font-bold text-gray-900">
                          {plan.price_monthly === 0 ? t('app.profile.plan.free') : formatPrice(plan.price_monthly)}
                        </span>
                        {plan.price_monthly > 0 && (
                          <span className="text-gray-500 text-sm">{t('app.profile.plan.perMonth')}</span>
                        )}
                      </div>

                      <ul className="mt-4 space-y-2">
                        {features?.map((feature, idx) => (
                          <li key={idx} className="flex items-start text-sm text-gray-600">
                            <CheckCircle size={16} className="mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                            {feature}
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={() => handleUpgrade(plan.id)}
                        disabled={isCurrentPlan}
                        className={`mt-4 w-full py-2 rounded-lg font-medium transition-colors ${
                          isCurrentPlan
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : plan.id === 'pro'
                              ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600'
                              : 'bg-sky-500 text-white hover:bg-sky-600'
                        }`}
                      >
                        {isCurrentPlan ? t('app.profile.subscription.currentPlanButton') : t('app.profile.subscription.choose')}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Billing History Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <CreditCard className="mr-2 text-sky-600" size={20} />
            {t('app.profile.billing.title')}
          </h2>
        </div>

        <div className="p-6">
          {invoicesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="animate-spin text-sky-600" size={24} />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="mx-auto mb-3 text-gray-300" size={40} />
              <p>{t('app.profile.billing.noInvoices')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-500 border-b">
                    <th className="pb-3 font-medium">{t('app.profile.billing.date')}</th>
                    <th className="pb-3 font-medium">{t('app.profile.billing.amount')}</th>
                    <th className="pb-3 font-medium">{t('app.profile.billing.status')}</th>
                    <th className="pb-3 font-medium">{t('app.profile.billing.invoice')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="text-sm">
                      <td className="py-3 text-gray-900">{formatDate(invoice.created_at)}</td>
                      <td className="py-3 text-gray-900">{formatPrice(invoice.amount_paid, invoice.currency)}</td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          invoice.status === 'paid' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {invoice.status === 'paid' ? t('app.profile.billing.paid') : invoice.status}
                        </span>
                      </td>
                      <td className="py-3">
                        {invoice.invoice_pdf && (
                          <a
                            href={invoice.invoice_pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sky-600 hover:text-sky-700 flex items-center"
                          >
                            <Download size={16} className="mr-1" />
                            PDF
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Danger Zone - Delete Account */}
      <div className="bg-white rounded-xl shadow-sm border border-red-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-red-200 bg-red-50">
          <h2 className="text-lg font-semibold text-red-700 flex items-center">
            <Trash2 className="mr-2" size={20} />
            {t('app.profile.dangerZone')}
          </h2>
        </div>

        <div className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-medium text-gray-900">{t('app.profile.delete.title')}</h3>
              <p className="text-sm text-gray-500 mt-1">
                {t('app.profile.delete.description')}
              </p>
            </div>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center flex-shrink-0 ml-4"
            >
              <Trash2 size={16} className="mr-2" />
              {t('app.profile.delete.button')}
            </button>
          </div>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start space-x-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="text-red-600" size={20} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('app.profile.delete.title')}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {t('app.profile.delete.modalDescription')}
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('app.profile.delete.confirmLabel')} <span className="font-bold text-red-600">{t('app.profile.delete.confirmWord')}</span>
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={t('app.profile.delete.confirmWord')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('app.profile.delete.cancel')}
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== t('app.profile.delete.confirmWord')}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('app.profile.delete.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileTab;
